import * as Sentry from '@sentry/react';
import { configureLogger, consoleSink, sentrySink, type LogSink } from '@repo/logger';

/**
 * Initialise Sentry for the SPA at module-load time.
 *
 * The DSN is injected at build time via Vite's `import.meta.env`
 * pipeline. Vite only exposes vars prefixed with `VITE_`, so we
 * pull from `VITE_SENTRY_DSN` (mirrors `EXPO_PUBLIC_SENTRY_DSN` on
 * mobile and `SENTRY_DSN` on the server).
 *
 * Production builds without a DSN intentionally don't crash — the
 * sink degrades to console-only and the entire app keeps running.
 * Same posture as `apps/mobile/lib/sentry/init.ts` and the server's
 * Sentry boot, so the contract reads consistently across surfaces.
 *
 * Why module-load and not inside a `useEffect`:
 *   - The earliest unhandled errors (provider construction, font
 *     load, dynamic chunk failures) happen before the first effect
 *     runs. Module-load init catches them.
 *   - Sentry registers `window.onerror` + `unhandledrejection`
 *     handlers during `init()`. Doing this from a render path
 *     would race React's own error-boundary plumbing.
 */
const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const environment = (import.meta.env.VITE_APP_ENV as string | undefined) ?? import.meta.env.MODE;
const release = import.meta.env.VITE_APP_VERSION as string | undefined;

let initialised = false;

function bootstrap(): boolean {
  if (initialised) return true;
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.5,
    enabled: import.meta.env.PROD,
  });
  initialised = true;
  return true;
}

const enabled = bootstrap();

const sinks: LogSink[] = [consoleSink];
if (enabled) {
  sinks.push(
    sentrySink({
      adapter: {
        captureMessage: (message, options) => {
          Sentry.captureMessage(message, {
            level: options.level,
            extra: options.extra,
            tags: options.tags,
          });
        },
        captureException: (error, options) => {
          Sentry.captureException(error, {
            level: options.level,
            extra: options.extra,
            tags: options.tags,
          });
        },
      },
      tags: { app: 'web' },
    }),
  );
}

configureLogger({
  level: import.meta.env.DEV ? 'debug' : 'info',
  sinks,
});

export const sentryEnabled = enabled;
export { Sentry };
